/**
 * heldAimInput — Held free-aim input state resolution, modifier consumption policy,
 * and keybinding conflict detection.
 */

import type { KeyBinding } from '../../../shared/CharacterTypes';
import type { ActionBindingMap } from './keyboardBindingMap';
import {
  MODIFIER_KEY_CODES,
  type ModifierFamily,
  getModifierFamilyFromCode,
  isGameplayAction,
  GAMEPLAY_ACTION_NAMES,
  GAMEPLAY_ACTIONS,
} from './keyboardModifierState';

export {
  MODIFIER_KEY_CODES,
  getModifierFamilyFromCode,
  isGameplayAction,
  GAMEPLAY_ACTION_NAMES,
  GAMEPLAY_ACTIONS,
};
export type { ModifierFamily };

export interface AimInputState {
  readonly freeAimHeld: boolean;
}

export const INACTIVE_AIM_INPUT_STATE: AimInputState = Object.freeze({
  freeAimHeld: false,
});

export const ACTIVE_AIM_INPUT_STATE: AimInputState = Object.freeze({
  freeAimHeld: true,
});

/**
 * Checks whether the configured `free_aim` binding is active in the currently held keys.
 */
export function isFreeAimHeld(
  actionToCodes: ActionBindingMap,
  keysDown: ReadonlySet<string>
): boolean {
  const configs = actionToCodes.get('free_aim');
  if (!configs || configs.length === 0) return false;

  return configs.some(item => {
    if (!keysDown.has(item.code)) return false;

    if (item.modifier) {
      if (item.modifier === 'shift' && !(keysDown.has(MODIFIER_KEY_CODES.SHIFT_LEFT) || keysDown.has(MODIFIER_KEY_CODES.SHIFT_RIGHT))) return false;
      if (item.modifier === 'ctrl' && !(keysDown.has(MODIFIER_KEY_CODES.CONTROL_LEFT) || keysDown.has(MODIFIER_KEY_CODES.CONTROL_RIGHT))) return false;
      if (item.modifier === 'alt' && !(keysDown.has(MODIFIER_KEY_CODES.ALT_LEFT) || keysDown.has(MODIFIER_KEY_CODES.ALT_RIGHT))) return false;
    }
    return true;
  });
}

/**
 * Resolves the modifier family consumed by the currently active free-aim binding, if any.
 * Returns null if free-aim is not held or if its binding does not consume a modifier (e.g. nonmodifier key).
 * Only returns a modifier from an actually satisfied held binding.
 */
export function getActiveFreeAimConsumedModifier(
  actionToCodes: ActionBindingMap,
  keysDown: ReadonlySet<string>
): ModifierFamily | null {
  const configs = actionToCodes.get('free_aim');
  if (!configs || configs.length === 0) return null;

  for (const item of configs) {
    if (!keysDown.has(item.code)) continue;

    if (item.modifier) {
      const modDown =
        (item.modifier === 'shift' && (keysDown.has(MODIFIER_KEY_CODES.SHIFT_LEFT) || keysDown.has(MODIFIER_KEY_CODES.SHIFT_RIGHT))) ||
        (item.modifier === 'ctrl' && (keysDown.has(MODIFIER_KEY_CODES.CONTROL_LEFT) || keysDown.has(MODIFIER_KEY_CODES.CONTROL_RIGHT))) ||
        (item.modifier === 'alt' && (keysDown.has(MODIFIER_KEY_CODES.ALT_LEFT) || keysDown.has(MODIFIER_KEY_CODES.ALT_RIGHT)));
      if (!modDown) continue;
      return item.modifier as ModifierFamily;
    }

    const keyMod = getModifierFamilyFromCode(item.code);
    if (keyMod) {
      return keyMod;
    }
  }
  return null;
}

/**
 * Detects whether rebinding `target` to `newKey` introduces a conflict with another binding or explicit chord.
 * Returns the conflicting binding, or null if safe.
 */
export function findBindingConflict(
  target: KeyBinding,
  newKey: string,
  bindings: readonly KeyBinding[]
): { action: string; key: string; modifier?: string } | null {
  const normalizedNewKey = newKey.toUpperCase();

  // If we are rebinding free_aim:
  // free_aim is held during gameplay, so it cannot share a key with any action or explicit chord.
  if (target.action === 'free_aim') {
    const conflict = bindings.find(b => {
      if (b.action === 'free_aim') return false;
      return b.key.toUpperCase() === normalizedNewKey;
    });
    if (conflict) {
      return conflict;
    }
  } else {
    // If we are rebinding another action to the key used by free_aim:
    const freeAimBinding = bindings.find(b => b.action === 'free_aim');
    if (freeAimBinding && freeAimBinding.key.toUpperCase() === normalizedNewKey && !target.modifier) {
      return freeAimBinding;
    }

    // Explicit chord collision: another binding with the same key and same explicit modifier
    if (target.modifier) {
      const sameChordConflict = bindings.find(b => {
        if (b === target || (b.action === target.action && b.key.toUpperCase() === target.key.toUpperCase() && b.modifier === target.modifier)) {
          return false;
        }
        return b.key.toUpperCase() === normalizedNewKey && b.modifier === target.modifier && b.action !== target.action;
      });
      if (sameChordConflict) {
        return sameChordConflict;
      }
    }
  }

  return null;
}
